import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { LOGIN, GOOGLE_LOGIN } from '../graphql';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, ArrowRight, BookOpen, Building2, Users, Wallet } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import AuraLogo from '../components/AuraLogo';

const FEATURE_PILLS = [
  { icon: BookOpen,  title: 'Study Hub',         desc: 'Notes, study materials and attendance tracking' },
  { icon: Building2, title: 'Campus Life',       desc: 'Announcements, food and cab sharing, housing' },
  { icon: Users,     title: 'Community',         desc: 'Forums and the campus feed' },
  { icon: Wallet,    title: 'Money & Documents', desc: 'Expense groups and a personal document vault' },
];

export default function LoginPage() {
  const [form, setForm]           = useState({ email: '', password: '' });
  const [showPass, setShowPass]   = useState(false);
  const [error, setError]         = useState('');
  const [gLoading, setGLoading]   = useState(false);
  const { login }                 = useAuth();
  const navigate                  = useNavigate();
  const [loginMutation, { loading }]       = useMutation(LOGIN);
  const [googleLoginMutation]              = useMutation(GOOGLE_LOGIN);

  // GIS credential flow: onSuccess yields a REAL ID token (credential) — the implicit
  // useGoogleLogin flow returns an access token, which the server's verifyIdToken rejects.
  const handleGoogleCredential = async (credentialResponse) => {
    setGLoading(true);
    setError('');
    try {
      const idToken = credentialResponse.credential;
      const { data, errors } = await googleLoginMutation({ variables: { idToken } });
      if (errors?.length) throw new Error(errors[0].message);
      if (!data?.googleLogin) throw new Error('Google login failed.');
      login(data.googleLogin.token, data.googleLogin.user);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Google sign-in failed. Please try email/password.');
    } finally {
      setGLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const { data, errors } = await loginMutation({ variables: form });
      if (errors?.length) throw new Error(errors[0].message);
      if (!data?.login) throw new Error('Login failed. Please check your credentials.');
      login(data.login.token, data.login.user);
      navigate('/');
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('status code') || msg.includes('NetworkError') || msg.includes('Failed to fetch')) {
        setError('Cannot reach server. Please try again in a moment.');
      } else {
        setError(msg || 'Login failed. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen flex bg-white dark:bg-surface-900">

      {/* ── Left panel — flat deep navy ── */}
      <div
        className="hidden lg:flex lg:w-[48%] relative flex-col justify-between p-14 overflow-hidden"
        style={{ background: '#0d3c78' }}
      >
        {/* Logo */}
        <Link to="/" className="relative z-10 flex items-center gap-3" style={{ textDecoration: 'none' }}>
          <AuraLogo size={36} />
          <span className="text-white font-bold text-xl tracking-tight">Aura</span>
        </Link>

        {/* Hero content */}
        <div className="relative z-10">
          <h1 className="text-4xl font-bold text-white leading-[1.15] mb-4">
            Your campus, organised.
          </h1>
          <p className="text-white/70 text-base leading-relaxed mb-10">
            Attendance, study materials, housing, expenses and campus community — in one place.
          </p>

          <div className="space-y-3">
            {FEATURE_PILLS.map(f => (
              <div key={f.title} className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/[0.07] border border-white/10">
                <span className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0">
                  <f.icon size={16} className="text-white" />
                </span>
                <div>
                  <p className="text-white font-semibold text-sm">{f.title}</p>
                  <p className="text-white/60 text-xs">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="relative z-10 text-white/40 text-xs">
          © 2024 Quantaiseed · <a href="mailto:contact@quantaiseed.com" className="hover:text-white/70 transition-colors">contact@quantaiseed.com</a>
        </p>
      </div>

      {/* ── Right panel — clean white form ── */}
      <div className="flex-1 flex flex-col justify-center items-center px-8 py-12 bg-white dark:bg-surface-900">

        {/* Mobile logo */}
        <div className="lg:hidden mb-10">
          <Link to="/" className="flex items-center gap-2.5" style={{ textDecoration: 'none' }}>
            <AuraLogo size={36} />
            <span className="font-bold text-gray-900 dark:text-white text-xl">Aura</span>
          </Link>
        </div>

        <div className="w-full max-w-[360px]">
          {/* Header */}
          <div className="mb-8">
            <p className="text-xs font-semibold text-brand-600 dark:text-brand-400 uppercase tracking-widest mb-2">Welcome back</p>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Sign in to Aura</h2>
            <p className="text-gray-400 dark:text-white/40 mt-1 text-sm">Enter your credentials to continue</p>
          </div>

          {/* Google Sign-In (GIS credential button — returns a real ID token) */}
          <div className="mb-4 flex justify-center">
            {gLoading ? (
              <span className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleCredential}
                onError={() => setError('Google sign-in was cancelled or failed.')}
                width="352"
                text="continue_with"
              />
            )}
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-gray-100 dark:bg-white/[0.06]" />
            <span className="text-xs text-gray-300 dark:text-white/20 font-medium">or sign in with email</span>
            <div className="flex-1 h-px bg-gray-100 dark:bg-white/[0.06]" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 dark:text-white/50 uppercase tracking-wide mb-2">
                Email address
              </label>
              <input
                className="input"
                type="email"
                placeholder="you@college.edu"
                required
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-gray-500 dark:text-white/50 uppercase tracking-wide">
                  Password
                </label>
                <Link to="/forgot-password"
                  className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  className="input pr-11"
                  type={showPass ? 'text' : 'password'}
                  placeholder="Enter your password"
                  required
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                />
                <button type="button" onClick={() => setShowPass(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 dark:text-white/25 hover:text-gray-500 dark:hover:text-white/50 transition-colors">
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40">
                <div className="w-4 h-4 rounded-full bg-red-100 dark:bg-red-800/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-red-500 text-[10px] font-black">!</span>
                </div>
                <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 rounded-lg font-semibold text-white text-sm flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-600 transition-colors active:scale-[0.98] disabled:opacity-50 mt-2"
            >
              {loading
                ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <><span>Sign in</span><ArrowRight size={15} /></>}
            </button>
          </form>

          <p className="text-center text-sm text-gray-400 dark:text-white/40 mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="font-semibold text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors">
              Create one
            </Link>
          </p>
          <p className="text-center text-xs text-gray-300 dark:text-white/25 mt-2">
            College admin or staff?{' '}
            <Link to="/admin" className="font-medium text-gray-400 dark:text-white/40 hover:text-brand-600 transition-colors">
              Admin console →
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
